import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import {
  BookOpen,
  UserCircle,
  Barcode,
  Plus,
  X,
  Download,
  Trash2,
  Check,
  Clock,
  XCircle,
  FileSpreadsheet,
  Loader2,
  History,
  Camera,
  ScanLine
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format date as mm/dd/yy
const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const mins = String(date.getMinutes()).padStart(2, '0');
    return `${month}/${day}/${year} ${hours}:${mins}`;
  } catch {
    return dateStr;
  }
};

export const MobileOfflineCheckout = ({ user }) => {
  const [checkouts, setCheckouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showHistory, setShowHistory] = useState(false);
  
  // New checkout form
  const [memberBarcode, setMemberBarcode] = useState("");
  const [memberName, setMemberName] = useState("");
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [currentBookBarcode, setCurrentBookBarcode] = useState("");
  const [currentBookTitle, setCurrentBookTitle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Scanner state
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scannerReady, setScannerReady] = useState(false);
  const [scanTarget, setScanTarget] = useState(null); // 'member' or 'book'
  const html5QrCodeRef = useRef(null);
  const scannerRef = useRef(null);
  
  const bookBarcodeRef = useRef(null);

  // Supported barcode formats
  const SUPPORTED_BARCODE_FORMATS = [
    Html5QrcodeSupportedFormats.EAN_13,
    Html5QrcodeSupportedFormats.EAN_8,
    Html5QrcodeSupportedFormats.UPC_A,
    Html5QrcodeSupportedFormats.UPC_E,
    Html5QrcodeSupportedFormats.CODE_39,
    Html5QrcodeSupportedFormats.CODE_128,
    Html5QrcodeSupportedFormats.CODABAR,
    Html5QrcodeSupportedFormats.ITF,
  ];

  // Clean Codabar start/stop characters
  const cleanBarcodeValue = (barcode) => {
    if (!barcode) return barcode;
    return barcode.replace(/^[ABCDabcd]/, '').replace(/[ABCDabcd]$/, '');
  };

  useEffect(() => {
    fetchCheckouts();
  }, []);

  // Scanner functions
  const startScanner = async (target) => {
    setScanTarget(target);
    setScannerOpen(true);
    setScannerReady(false);
    
    // Wait for dialog to render the element
    setTimeout(async () => {
      try {
        const element = document.getElementById("mobile-barcode-reader");
        if (!element) {
          console.error("Scanner element not found");
          toast.error("Scanner element not ready");
          return;
        }
        
        html5QrCodeRef.current = new Html5Qrcode("mobile-barcode-reader", {
          formatsToSupport: SUPPORTED_BARCODE_FORMATS,
          verbose: false
        });
        
        // Get container width for responsive qrbox
        const containerWidth = element.offsetWidth || 300;
        const qrboxWidth = Math.min(containerWidth - 40, 280);
        const qrboxHeight = Math.min(100, qrboxWidth * 0.4);
        
        await html5QrCodeRef.current.start(
          { facingMode: "environment" },
          {
            fps: 15,  // Higher FPS for faster detection
            qrbox: { width: qrboxWidth, height: qrboxHeight },
            aspectRatio: 1.0,  // Square aspect ratio works better on mobile
            disableFlip: false,
            experimentalFeatures: {
              useBarCodeDetectorIfSupported: true  // Use native BarcodeDetector API if available
            }
          },
          (decodedText, decodedResult) => {
            const cleanedBarcode = cleanBarcodeValue(decodedText);
            console.log("Scanned:", decodedText, "Cleaned:", cleanedBarcode, "Format:", decodedResult?.result?.format?.formatName);
            
            if (target === 'member') {
              setMemberBarcode(cleanedBarcode);
              toast.success(`Member barcode: ${cleanedBarcode}`);
            } else if (target === 'book') {
              setCurrentBookBarcode(cleanedBarcode);
              toast.success(`Book barcode: ${cleanedBarcode}`);
            }
            
            stopScanner();
          },
          (errorMessage) => {
            // Ignore scan errors (normal during scanning)
          }
        );
        setScannerReady(true);
      } catch (err) {
        console.error("Failed to start scanner:", err);
        toast.error("Could not access camera");
        setScannerOpen(false);
      }
    }, 300);  // Increased delay for DOM to be ready
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current.clear();
      } catch (err) {
        // Ignore stop errors
      }
      html5QrCodeRef.current = null;
    }
    setScannerOpen(false);
    setScannerReady(false);
    setScanTarget(null);
  };

  const fetchCheckouts = async () => {
    try {
      const response = await axios.get(`${API}/offline-checkout/list`);
      setCheckouts(response.data);
    } catch (error) {
      toast.error("Failed to fetch checkouts");
    } finally {
      setLoading(false);
    }
  };

  const addBookItem = () => {
    if (!currentBookBarcode.trim()) {
      toast.error("Please enter a book barcode");
      return;
    }
    
    if (items.some(item => item.book_barcode === currentBookBarcode.trim())) {
      toast.error("This book is already added");
      return;
    }
    
    setItems([...items, {
      book_barcode: currentBookBarcode.trim(),
      book_title: currentBookTitle.trim() || null
    }]);
    
    setCurrentBookBarcode("");
    setCurrentBookTitle("");
    bookBarcodeRef.current?.focus();
    toast.success("Book added");
  };

  const removeBookItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmitCheckout = async (downloadCSV = false) => {
    if (!memberBarcode.trim()) {
      toast.error("Please enter member barcode");
      return;
    }
    if (items.length === 0) {
      toast.error("Please add at least one book");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      if (downloadCSV) {
        const response = await axios.post(
          `${API}/offline-checkout/batch/csv`,
          {
            member_barcode: memberBarcode.trim(),
            member_name: memberName.trim() || null,
            items: items,
            notes: notes.trim() || null
          },
          { responseType: 'blob' }
        );
        
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.download = `checkout_${memberBarcode}_${new Date().toISOString().slice(0,10)}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        toast.success("CSV downloaded");
      } else {
        await axios.post(`${API}/offline-checkout/create`, {
          member_barcode: memberBarcode.trim(),
          member_name: memberName.trim() || null,
          items: items,
          notes: notes.trim() || null
        });
        
        toast.success("Checkout saved");
      }
      
      // Reset form
      setMemberBarcode("");
      setMemberName("");
      setNotes("");
      setItems([]);
      fetchCheckouts();
      
    } catch (error) {
      toast.error("Failed to process checkout");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportAll = async (status = "pending") => {
    try {
      const response = await axios.get(
        `${API}/offline-checkout/export/csv?status=${status}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `checkouts_${status}_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("CSV exported");
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  const handleUpdateStatus = async (checkoutId, status) => {
    try {
      await axios.put(`${API}/offline-checkout/${checkoutId}/status?status=${status}`);
      toast.success(`Marked as ${status}`);
      fetchCheckouts();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (checkoutId) => {
    if (!confirm("Delete this checkout?")) return;
    
    try {
      await axios.delete(`${API}/offline-checkout/${checkoutId}`);
      toast.success("Deleted");
      fetchCheckouts();
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case "synced":
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200"><Check className="h-3 w-3 mr-1" />Synced</Badge>;
      case "cancelled":
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200"><XCircle className="h-3 w-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const pendingCount = checkouts.filter(c => c.status === "pending").length;

  return (
    <div className="px-4 py-4 pb-24" data-testid="mobile-offline-checkout">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading font-semibold text-stone-800 flex items-center gap-2">
            <Barcode className="h-5 w-5 brand-blue" />
            Offline Checkout
          </h2>
          <p className="text-sm text-muted-foreground">Scan member & book barcodes</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowHistory(true)}
          className="relative"
          data-testid="show-history-btn"
        >
          <History className="h-4 w-4 mr-1" />
          History
          {pendingCount > 0 && (
            <Badge className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs bg-amber-500">
              {pendingCount}
            </Badge>
          )}
        </Button>
      </div>

      {/* Member Card */}
      <Card className="mb-4 border-0 shadow-md">
        <div className="h-1 bg-gradient-to-r from-[#4B8BC7] to-[#6B4E8D]" />
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 text-[#4B8BC7] font-medium">
            <UserCircle className="h-5 w-5" />
            Member
          </div>
          
          <div>
            <Label className="text-xs text-muted-foreground">Member Barcode *</Label>
            <div className="flex gap-2 mt-1">
              <Input
                placeholder="Scan or type barcode"
                value={memberBarcode}
                onChange={(e) => setMemberBarcode(e.target.value)}
                className="font-mono flex-1"
                data-testid="mobile-member-barcode"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => startScanner('member')}
                className="flex-shrink-0"
                data-testid="scan-member-btn"
              >
                <Camera className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <div>
            <Label className="text-xs text-muted-foreground">Name (optional)</Label>
            <Input
              placeholder="Member name"
              value={memberName}
              onChange={(e) => setMemberName(e.target.value)}
              className="mt-1"
            />
          </div>
        </CardContent>
      </Card>

      {/* Books Card */}
      <Card className="mb-4 border-0 shadow-md">
        <div className="h-1 bg-gradient-to-r from-[#6B4E8D] to-[#C4453A]" />
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-[#6B4E8D] font-medium">
              <BookOpen className="h-5 w-5" />
              Books
            </div>
            {items.length > 0 && (
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                {items.length} added
              </Badge>
            )}
          </div>
          
          {/* Add book form */}
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                ref={bookBarcodeRef}
                placeholder="Book barcode *"
                value={currentBookBarcode}
                onChange={(e) => setCurrentBookBarcode(e.target.value)}
                className="font-mono flex-1"
                data-testid="mobile-book-barcode"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => startScanner('book')}
                className="flex-shrink-0"
                data-testid="scan-book-btn"
              >
                <Camera className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Title (optional)"
                value={currentBookTitle}
                onChange={(e) => setCurrentBookTitle(e.target.value)}
                className="flex-1"
              />
              <Button 
                onClick={addBookItem}
                className="bg-[#6B4E8D] hover:bg-[#6B4E8D]/90"
                data-testid="mobile-add-book-btn"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Book list */}
          {items.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground border-2 border-dashed rounded-lg">
              <BookOpen className="h-6 w-6 mx-auto mb-1 opacity-50" />
              <p className="text-sm">No books added</p>
            </div>
          ) : (
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {items.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-2 rounded-lg bg-purple-50 border border-purple-200"
                >
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Barcode className="h-4 w-4 text-purple-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="font-mono text-sm truncate">{item.book_barcode}</p>
                      {item.book_title && (
                        <p className="text-xs text-muted-foreground truncate">{item.book_title}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeBookItem(index)}
                    className="text-red-500 hover:text-red-700 flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      <Card className="mb-6 border-0 shadow-md">
        <CardContent className="p-4">
          <Label className="text-xs text-muted-foreground">Notes (optional)</Label>
          <Textarea
            placeholder="Any additional notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="mt-1"
          />
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="space-y-2">
        <Button
          onClick={() => handleSubmitCheckout(true)}
          disabled={isSubmitting || !memberBarcode || items.length === 0}
          className="w-full bg-[#4B8BC7] hover:bg-[#4B8BC7]/90 h-12"
          data-testid="mobile-download-csv-btn"
        >
          {isSubmitting ? (
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
          ) : (
            <Download className="h-5 w-5 mr-2" />
          )}
          Save & Download CSV
        </Button>
        
        <Button
          variant="outline"
          onClick={() => handleSubmitCheckout(false)}
          disabled={isSubmitting || !memberBarcode || items.length === 0}
          className="w-full h-12"
        >
          {isSubmitting ? (
            <Loader2 className="h-5 w-5 mr-2 animate-spin" />
          ) : (
            <Check className="h-5 w-5 mr-2" />
          )}
          Save Only
        </Button>
      </div>

      {/* History Sheet */}
      <Sheet open={showHistory} onOpenChange={setShowHistory}>
        <SheetContent side="bottom" className="h-[85vh] rounded-t-3xl">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="flex items-center justify-between">
              <span>Checkout History</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => handleExportAll("pending")}>
                  <FileSpreadsheet className="h-4 w-4 mr-1" />
                  Pending
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleExportAll("")}>
                  <Download className="h-4 w-4 mr-1" />
                  All
                </Button>
              </div>
            </SheetTitle>
          </SheetHeader>
          
          <div className="py-4 overflow-y-auto max-h-[calc(85vh-100px)]">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-[#4B8BC7]" />
              </div>
            ) : checkouts.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Barcode className="h-10 w-10 mx-auto mb-2 opacity-50" />
                <p>No checkouts yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {checkouts.map((checkout) => (
                  <Card key={checkout.id} className="border shadow-sm">
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-mono text-sm font-medium">{checkout.member_barcode}</p>
                            {getStatusBadge(checkout.status)}
                          </div>
                          {checkout.member_name && (
                            <p className="text-sm text-muted-foreground">{checkout.member_name}</p>
                          )}
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                            <span>{formatDate(checkout.checkout_date)}</span>
                            <Badge variant="outline" className="text-xs">
                              {checkout.items?.length || 0} books
                            </Badge>
                          </div>
                        </div>
                        
                        <div className="flex gap-1">
                          {checkout.status === "pending" && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleUpdateStatus(checkout.id, "synced")}
                              className="h-8 w-8"
                            >
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(checkout.id)}
                            className="h-8 w-8 text-red-500"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Barcode Scanner Dialog */}
      <Dialog open={scannerOpen} onOpenChange={(open) => !open && stopScanner()}>
        <DialogContent className="max-w-sm mx-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="h-5 w-5" />
              Scan {scanTarget === 'member' ? 'Member' : 'Book'} Barcode
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Center the barcode in the box - it will be detected automatically
            </p>
            
            <div 
              id="mobile-barcode-reader" 
              ref={scannerRef}
              className="w-full aspect-square bg-black rounded-lg overflow-hidden"
              style={{ minHeight: '280px' }}
            />
            
            {!scannerReady && (
              <div className="flex items-center justify-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <p className="text-sm">Starting camera...</p>
              </div>
            )}
            
            {scannerReady && (
              <p className="text-xs text-center text-emerald-600 font-medium">
                Camera active - scanning for barcodes...
              </p>
            )}
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  const manual = prompt(`Enter ${scanTarget === 'member' ? 'member' : 'book'} barcode manually:`);
                  if (manual) {
                    if (scanTarget === 'member') {
                      setMemberBarcode(manual.trim());
                    } else {
                      setCurrentBookBarcode(manual.trim());
                    }
                    stopScanner();
                    toast.success("Barcode entered");
                  }
                }}
              >
                Enter Manually
              </Button>
              <Button
                variant="outline"
                className="flex-1"
                onClick={stopScanner}
              >
                Cancel
              </Button>
            </div>
            
            <p className="text-xs text-center text-muted-foreground">
              Supports: ISBN, UPC, Code 39, Code 128, Codabar
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileOfflineCheckout;
